import { DependencyList, useCallback, useContext, useEffect, useId, useMemo } from 'react';

import { DialogContext } from './DialogProvider';
import { DialogOptions } from './types';

export const useDialog = (
    options?: Omit<DialogOptions, 'resolve' | 'reject'>,
    deps: DependencyList = [],
): ((options?: DialogOptions | ((close: () => void) => DialogOptions)) => Promise<void>) => {
    const id = useId();
    const handlers = useContext(DialogContext);

    if (!handlers) {
        throw new Error('useDialog must be used within a DialogProvider');
    }

    // eslint-disable-next-line react-compiler/react-compiler, react-hooks/exhaustive-deps
    const memoizedOptions = useMemo(() => options, [...deps]);

    const closeModalHandler = useCallback(() => {
        handlers.close?.(id);
    }, [handlers, id]);

    const openModalHandler = useCallback(
        (options?: DialogOptions | ((close: () => void) => DialogOptions)) => {
            let newOptions: DialogOptions | undefined;

            if (typeof options === 'function') {
                newOptions = options(closeModalHandler);
            } else {
                newOptions = options;
            }

            return handlers.open?.(id, {
                ...memoizedOptions,
                ...newOptions,
            });
        },
        [closeModalHandler, handlers, id, memoizedOptions],
    );

    useEffect(() => {
        handlers?.update?.(id, memoizedOptions);
    }, [id, memoizedOptions, handlers]);

    useEffect(
        () => () => {
            handlers?.clear?.(id);
        },
        [id, handlers],
    );

    return openModalHandler;
};
